import React, { useMemo } from 'react';

import { CheckOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { schemas } from '@recurrency/core-api-schema';
import { PurchaseTargetLineStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { PurchaseTargetLineDTO } from '@recurrency/core-api-schema/dist/purchasing/getPurchaseTargetLines';
import { ItemAvailabilityDTO } from '@recurrency/core-api-schema/dist/v2/items/getItemAvailability';
import { ColumnType } from 'antd/lib/table';
import { theme } from 'theme';

import { DEFAULT_NUM_ROWS_SHOWN, MiniTable } from 'components/MiniTable';
import { NetStockPopover, NetStockRecord } from 'components/recipes/equation/NetStockPopover';
import { ViewAllModal } from 'components/recipes/sidePane/ViewAllModal';
import { TargetLineStatusBadge } from 'components/recipes/TargetLineStatusBadge';
import { Table } from 'components/Table';
import { IconTooltip } from 'components/Tooltip/IconTooltip';

import { useCoreApi } from 'hooks/useApi';

import { showAsyncModal } from 'utils/asyncModal';
import { formatNumber } from 'utils/formatting';
import { numberColumn, typedColumn } from 'utils/tables';

import { QtyToOrderPopover } from '../../../../../components/recipes/equation/QtyToOrderPopover';
import { GenericTargetLineDTO } from '../../types';
import { useAggregatedNetstockEnabled } from './utils';

export interface TargetLineWOverUnderBy extends PurchaseTargetLineDTO {
  overUnderBy: number;
}

export function LocationStockPanelContent({ targetLine }: { targetLine: GenericTargetLineDTO }) {
  const {
    data: targetLinesData,
    isLoading: isTargetLinesLoading,
    error: targetLinesError,
  } = useCoreApi(schemas.purchasing.getPurchaseTargetLines, {
    queryParams: {
      filter: {
        itemIds: [targetLine.itemId],
      },
    },
  });

  const targetLines: TargetLineWOverUnderBy[] = useMemo(
    () =>
      (targetLinesData?.items || [])
        .map((record) => ({
          ...record,
          overUnderBy:
            record.netStock > record.invMax
              ? record.netStock - record.invMax
              : record.netStock < record.invMin
              ? record.netStock - record.invMin
              : 0,
        }))
        .sort((a, b) =>
          // sort by overUnderBy, then by locationId
          a.overUnderBy - b.overUnderBy === 0
            ? a.locationId.localeCompare(b.locationId)
            : (a.overUnderBy - b.overUnderBy) *
              // if targetLine is overstock, show understocked locations first, otherwise show overstocked locations first
              (targetLine.status === PurchaseTargetLineStatus.Understock ? -1 : 1),
        ),
    [targetLine.status, targetLinesData?.items],
  );

  return (
    <MiniTable
      numRowsShown={DEFAULT_NUM_ROWS_SHOWN}
      data={targetLines}
      columns={[
        {
          render: (record) => `${record.locationId}: ${record.locationName}`,
          maxWidth: '130px',
        },
        {
          render: (record) =>
            record.status +
            (record.status === PurchaseTargetLineStatus.Overstock ||
            record.status === PurchaseTargetLineStatus.Understock
              ? ` by ${formatNumber(Math.abs(record.overUnderBy), 0)}`
              : ''),
          align: 'left',
        },
        {
          render: (record) => `${formatNumber(record.qtyAvailable)} ${record.unitOfMeasure || ``}`,
          align: 'right',
        },
      ]}
      isLoading={isTargetLinesLoading}
      error={targetLinesError}
      onMoreButtonClick={() => {
        showAsyncModal(ViewAllModal, {
          title: 'Location Stock',
          content: <LocationStockTable targetLines={targetLines} />,
        });
      }}
    />
  );
}

function LocationStockTable({ targetLines }: { targetLines: TargetLineWOverUnderBy[] }) {
  const { enabled: aggregatedNetStockEnabled } = useAggregatedNetstockEnabled();

  // Gets the non aggregated stock values for tenants with netstock aggregation
  // For tenants without netstock aggregation, the result of this call is not used
  const { data: nonAggregatedData, isLoading: isLoadingNonAggregatedData } = useCoreApi(
    schemas.items.getItemAvailabilityV2,
    {
      pathParams: {
        itemUid: targetLines[0].itemUid,
      },
    },
  );
  const targetLinesWithStockQuantities: Record<string, NetStockRecord> = useMemo(
    () =>
      (nonAggregatedData?.items || []).reduce((acc, curr: ItemAvailabilityDTO) => {
        acc[curr.locationId] = {
          netStock: curr.netStock,
          qtyOnHand: curr.qtyOnHand,
          aggregatedNetStock: curr.netStock,
          qtyAllocated: curr.qtyAllocated ?? 0,
          qtyOnPurchaseOrder: curr.qtyOnPurchaseOrder,
          qtyOnUnapprovedPurchaseOrder: curr.qtyOnUnapprovedPurchaseOrder,
          qtyInTransit: curr.qtyInTransit ?? 0,
          qtyInProcess: curr.qtyInProcess ?? 0,
          qtyBackordered: curr.qtyBackordered ?? 0,
          qtyReservedDueIn: curr.qtyReservedDueIn,
          qtyInProduction: curr.qtyInProduction,
          qtyForProduction: curr.qtyForProduction,
          qtyQuarantined: curr.qtyQuarantined,
          qtyFrozen: curr.qtyFrozen,
          qtyNonPickable: curr.qtyNonPickable,
          qtyOnReleaseSchedule: curr.qtyOnReleaseSchedule,
          qtyOnPendingBackorder: curr.qtyOnPendingBackorder,
          qtyOnBlanketPurchaseOrder: curr.qtyOnBlanketPurchaseOrder,
          unitOfMeasure: curr.purchaseUnitOfMeasure ?? '',
        };
        return acc;
      }, {} as Record<string, NetStockRecord>),
    [nonAggregatedData?.items],
  );

  const columns: ColumnType<TargetLineWOverUnderBy>[] = [
    typedColumn({
      key: 'locationId',
      title: 'Location',
      dataIndex: 'locationId',
      render: (_, record) => `${record.locationId}: ${record.locationName}`,
    }),
    typedColumn({
      key: 'unitOfMeasure',
      title: 'UOM',
      dataIndex: 'unitOfMeasure',
      align: 'left' as const,
      render: (value) => value || '-',
    }),
    typedColumn({
      key: 'stockable',
      title: 'Stockable',
      dataIndex: 'stockable',
      align: 'center' as const,
      render: (value: boolean) => (value ? <CheckOutlined /> : null),
    }),
    typedColumn({
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center' as const,
      render: (status) => <TargetLineStatusBadge status={status} />,
    }),
    ...(aggregatedNetStockEnabled
      ? [
          numberColumn({
            title: () => (
              <>
                Net Stock
                <IconTooltip
                  tooltipText="Sum of net stock at this location and all downstream spokes it replenishes"
                  wrapText
                  Icon={QuestionCircleOutlined}
                  iconStyle={{ margin: '4px', color: theme.colors.neutral[900] }}
                />
              </>
            ),
            dataIndex: 'aggregatedNetStock',
            align: 'left',
            key: 'aggregatedNetStock',
            render: (value: number, record: TargetLineWOverUnderBy) => (
              <>
                {formatNumber(value)} {record.unitOfMeasure}
                <NetStockPopover record={record} isAggregatedNetStock />
              </>
            ),
          }),

          numberColumn({
            title: 'Location Stock',
            dataIndex: 'netStock',
            render: (value, record) =>
              record.locationId in targetLinesWithStockQuantities ? (
                <>
                  {formatNumber(targetLinesWithStockQuantities?.[record.locationId]?.netStock ?? value)}{' '}
                  {record?.unitOfMeasure ?? targetLinesWithStockQuantities[record.locationId].unitOfMeasure}
                  <NetStockPopover
                    key={`location_ns_agg_pop_${record.locationId}`}
                    record={targetLinesWithStockQuantities[record.locationId]}
                  />
                </>
              ) : (
                <>
                  {formatNumber(value)} {record.unitOfMeasure}
                  <NetStockPopover record={record} />
                </>
              ),
            align: 'left',
          }),
        ]
      : [
          numberColumn({
            title: 'Net Stock',
            key: 'netStock',
            dataIndex: ['netStock'],
            render: (value, record) => (
              <>
                {formatNumber(value)} {record?.unitOfMeasure}
                <NetStockPopover key={`location_ns_pop_${record.locationId}`} record={record} />
              </>
            ),
            align: 'left',
          }),
        ]),
    typedColumn({
      key: 'invMin',
      title: 'Min',
      dataIndex: 'invMin',
      align: 'right' as const,
      render: (value) => formatNumber(value),
    }),
    typedColumn({
      key: 'invMax',
      title: 'Max',
      dataIndex: 'invMax',
      align: 'right' as const,
      render: (value) => formatNumber(value),
    }),
    typedColumn({
      key: 'overUnderBy',
      title: 'Under/Over by',
      dataIndex: 'overUnderBy',
      align: 'right' as const,
      render: (value) => formatNumber(value),
    }),
    typedColumn({
      key: 'qtyToOrder',
      title: 'Qty to Order',
      dataIndex: 'qtyToOrder',
      align: 'right' as const,
      render: (value, record) => (
        <>
          {formatNumber(value)}
          <QtyToOrderPopover record={record} isAggregatedNetStock={aggregatedNetStockEnabled} />
        </>
      ),
    }),
    typedColumn({
      title: 'Qty Available',
      dataIndex: 'qtyAvailable',
      align: 'right' as const,
      render: (value) => formatNumber(value),
    }),
  ];
  return (
    <Table
      rowKey={({ locationId }) => locationId}
      isLoading={isLoadingNonAggregatedData}
      data={targetLines}
      columns={columns}
    />
  );
}
